import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  PageLoader,
  useSnackbars,
} from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { PlanGuard } from '@/components/guards';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { useAuth } from '@/services/auth';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { DocumentTitle } from '@/components/page/document-title';
import { useBreadcrumbTitle } from '@/services/breadcrumbs';
import { isEmptyString } from '@/utils/string';
import { PlanFeature } from '@/services/auth/types';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { generatePath } from '@/services/router/generate-path';
import { getWorkspaceIdFromUrl } from '@/utils/workspace-id';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { hasPlanFeatureEnabled } from '@/services/auth/plan-features';
import { getWarehouseSyncQueryOptions } from '@/services/db/warehouse-sync/queries';
import { getWarehouseDestinationName } from '@/features/warehouse-sync';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import RedshiftPage from './redshift.page';
import BigQueryPage from './bigquery.page';
import SnowflakePage from './snowflake.page';
import ConfigPage from './config.page';
import CreatePage from './create.page';
import { useWarehouseConfigBreadcrumbs, useWarehouseConnectorBreadcrumbs } from './hooks';
import { toWarehouseDestination } from './types';
import { WAREHOUSE_CONNECTOR_NAMES, type WarehouseConnector } from './types';
import type { WarehouseDestination } from '@/services/db/warehouse-sync/entity';

const LOADING_LABEL = 'Loading warehouse integration…';

/** Spelled out per connector: the route paths must be literals the router can resolve. */
const CREATE_ROUTE_PATH = '/workspaces/$workspaceId/integrations/warehouse/$type/sync';
const CONFIG_ROUTE_PATH = '/workspaces/$workspaceId/integrations/warehouse/$type';

const MIGRATED_ROUTE_PATHS = {
  redshift: '/workspaces/$workspaceId/integrations/warehouse/redshift',
  bigquery: '/workspaces/$workspaceId/integrations/warehouse/bigquery',
  snowflake: '/workspaces/$workspaceId/integrations/warehouse/snowflake',
} as const satisfies Record<WarehouseConnector, string>;

/**
 * The migrated twin of the legacy URL currently on screen — the same page with the `journeys`
 * segment dropped. Only this direction is needed: Hydra owns both URL shapes now, and the legacy
 * one exists solely to forward here.
 */
function useHandoffHref(connector: WarehouseConnector): string | null {
  const router = useRouter();
  const { pathname, searchStr, hash } = useLocation();
  const workspaceId = getWorkspaceIdFromUrl(pathname);

  if (workspaceId == null) {
    return null;
  }

  const to = MIGRATED_ROUTE_PATHS[connector];

  const href = router.buildLocation({ to, params: { workspaceId } }).href;
  // Ember transitions preserve query params, so both directions carry the query string and hash.
  return `${href}${searchStr}${hash === '' ? '' : `#${hash}`}`;
}

type JourneysWarehouseHandoffProps = {
  connector: WarehouseConnector;
};

/**
 * The old `/journeys/integrations/warehouse/<connector>` URLs. Hydra claims them purely to forward
 * to the migrated route, so Ember-side links keep working without every caller being re-pointed.
 */
export function JourneysWarehouseHandoff({ connector }: JourneysWarehouseHandoffProps) {
  const to = useHandoffHref(connector);

  // `/workspaces/last` is rewritten by a bridge-synced navigation that leaves route params stale,
  // so the id is read off the reactive location and held until it lands.
  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

/**
 * The sync read both warehouse pages perform, plus the Premium flag they both
 * show. Returns a ready-to-render fallback when the read has not resolved.
 */
function useWarehouseSyncPageState(workspaceId: string) {
  const auth = useAuth();
  // Keyed on the workspace the route view already resolved, rather than re-deriving it:
  // `requireWorkspaceId` throws while the `/workspaces/last` sentinel leaves route params stale.
  const { data: sync, isLoading, isError } = useQuery(getWarehouseSyncQueryOptions(Number(workspaceId)));

  // Blocked on the read rather than rendering against an unresolved record: a first paint with
  // `sync == null` would tell a connected workspace it has none and offer to connect a bucket it
  // already has. The query does not retry, so an error is final and gets its own state.
  let fallback: ReactNode = null;
  if (isLoading) {
    fallback = <PageLoader label={LOADING_LABEL} />;
  } else if (isError) {
    fallback = (
      <EmptyState>
        <EmptyStateTitle>We couldn't load your warehouse sync</EmptyStateTitle>
        <EmptyStateDescription>Refresh the page to try again.</EmptyStateDescription>
      </EmptyState>
    );
  }

  return {
    sync: sync ?? null,
    fallback,
    // Not `!enabled`: the label also covers premium-trial accounts, which carry the feature but
    // still see the upgrade pill. `augmentAccount` already folds that in.
    showPremiumLabel: auth.account?.planFeatures[PlanFeature.WarehouseSync]?.showPremiumLabel ?? true,
  };
}

type WarehouseRouteFrameProps = {
  children: (workspaceId: string) => ReactNode;
};

/**
 * The gating both warehouse route views share: hold until the workspace resolves, then apply the
 * gates Ember's `integrations` root route did.
 */
function WarehouseRouteFrame({ children }: WarehouseRouteFrameProps) {
  const workspace = useCurrentWorkspace();

  // Gate on the resolved workspace, not the URL segment: the pages' reads go through
  // `requireWorkspaceId`, which throws while the workspaces list is still loading and while the
  // `/workspaces/last` sentinel leaves route params stale.
  if (workspace == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }
  const workspaceId = String(workspace.id);

  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <ProtectedRoute
        permission="journeys/imports/view"
        redirectTo={generatePath('/workspaces/:workspaceId', { workspaceId })}
      >
        <WarehouseDocumentTitle />
        {children(workspaceId)}
      </ProtectedRoute>
    </PlanGuard>
  );
}

/**
 * The tab title, read back from the crumb chain each warehouse body registers —
 * leaf first, as the app being replaced titled it. Rendered once for the whole
 * subtree, after the guards, so a redirected visit never retitles the tab.
 */
function WarehouseDocumentTitle() {
  const title = useBreadcrumbTitle();
  return isEmptyString(title) ? null : <DocumentTitle title={title} />;
}

type WarehouseLandingBodyProps = {
  workspaceId: string;
  connector: WarehouseConnector;
};

function WarehouseLandingBody({ workspaceId, connector }: WarehouseLandingBodyProps) {
  useWarehouseConnectorBreadcrumbs(workspaceId, connector);
  const { track } = useAnalytics();
  const { sync, fallback, showPremiumLabel } = useWarehouseSyncPageState(workspaceId);

  usePageViewTracking(() => {
    void track('warehouse', 'destinationViewed', { Type: WAREHOUSE_CONNECTOR_NAMES[connector] });
  });

  if (fallback != null) {
    return fallback;
  }

  const props = { workspaceId, sync, showPremiumLabel };

  switch (connector) {
    case 'redshift':
      return (
        <RumPageRendered>
          <RedshiftPage {...props} />
        </RumPageRendered>
      );
    case 'bigquery':
      return (
        <RumPageRendered>
          <BigQueryPage {...props} />
        </RumPageRendered>
      );
    case 'snowflake':
      return (
        <RumPageRendered>
          <SnowflakePage {...props} />
        </RumPageRendered>
      );
    default: {
      const _exhaustive: never = connector;
      return String(_exhaustive);
    }
  }
}

type WarehouseLandingRouteViewProps = {
  connector: WarehouseConnector;
};

/**
 * The migrated `/integrations/warehouse/<connector>` URLs.
 *
 * Hydra owns the page, applying the gates Ember's `integrations` root route did (the landing
 * pages nest under it): Builder-plan accounts go to the available-plans page, and accounts
 * missing `journeys/imports/view` are redirected away.
 *
 * There is deliberately no warehouse-sync *plan* gate here. Ember puts that only on the
 * `warehouse/:type` routes, so a workspace without the feature can still read these pages — it is
 * bounced when it clicks through to connect a bucket. Adding the gate here would hide the upgrade
 * pitch from exactly the accounts it is written for.
 */
export function WarehouseLandingRouteView({ connector }: WarehouseLandingRouteViewProps) {
  return (
    <WarehouseRouteFrame>
      {(workspaceId) => <WarehouseLandingBody workspaceId={workspaceId} connector={connector} />}
    </WarehouseRouteFrame>
  );
}

/**
 * The workspace's enabled actions decide whether the `inbound` export is offered
 * at all — a v5 sync without Twilio hides the row rather than disabling it.
 */
function useIsTwilioEnabled(workspaceId: string): boolean {
  const environment = useEnvironmentOptional(Number(workspaceId));
  return environment.data?.environment?.enabled_actions?.includes('twilio') ?? false;
}

/** Build the migrated twin of the current URL for a `:type`-keyed route. */
function useTypedHandoffHref(migratedPath: string, type: string): string | null {
  const router = useRouter();
  const { pathname, searchStr, hash } = useLocation();
  const workspaceId = getWorkspaceIdFromUrl(pathname);

  if (workspaceId == null) {
    return null;
  }

  const href = router.buildLocation({ to: migratedPath, params: { workspaceId, type } }).href;
  // Ember transitions preserve query params, so both directions carry them.
  return `${href}${searchStr}${hash === '' ? '' : `#${hash}`}`;
}

type JourneysWarehouseConfigHandoffProps = {
  type: string;
};

/** The legacy `/journeys/integrations/warehouse/:type` URL — see the connector twin above. */
export function JourneysWarehouseConfigHandoff({ type }: JourneysWarehouseConfigHandoffProps) {
  const to = useTypedHandoffHref(CONFIG_ROUTE_PATH, type);

  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }
  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

type WarehouseConfigBodyProps = {
  workspaceId: string;
  destination: WarehouseDestination;
};

function WarehouseConfigBody({ workspaceId, destination }: WarehouseConfigBodyProps) {
  useWarehouseConfigBreadcrumbs(workspaceId, destination);
  const { track } = useAnalytics();
  const isTwilioEnabled = useIsTwilioEnabled(workspaceId);
  const { sync, fallback, showPremiumLabel } = useWarehouseSyncPageState(workspaceId);

  // Which of the two names this view reports depends on whether a sync exists, so it
  // waits for the read to settle rather than guessing on the first paint.
  usePageViewTracking(() => {
    void track('warehouse', sync == null ? 'emptyStateViewed' : 'settingsViewed', {
      Type: getWarehouseDestinationName(destination),
    });
  }, fallback == null);

  if (fallback != null) {
    return fallback;
  }

  return (
    <RumPageRendered>
      <ConfigPage
        workspaceId={workspaceId}
        destination={destination}
        sync={sync}
        showPremiumLabel={showPremiumLabel}
        isTwilioEnabled={isTwilioEnabled}
      />
    </RumPageRendered>
  );
}

type WarehouseConfigRouteViewProps = {
  type: string;
};

/**
 * The migrated `/integrations/warehouse/:type` config view.
 *
 * Unlike the connector landing pages, this route *does* carry the warehouse-sync plan gate — Ember
 * puts `redirectIfNoAccessToWarehouseSync` on `warehouse/:type` specifically, so an account without
 * the feature is bounced back to the integrations directory rather than shown the connect flow.
 */
type PlanDeniedRedirectProps = {
  to: string;
};

/**
 * The plan-gate bounce. Vanishing silently leaves the account with no idea why,
 * so it says so on the way out.
 *
 * The toast is raised from an effect rather than from render — a render-phase
 * `create` runs again on every re-render, and twice under StrictMode, before the
 * redirect commits. Rendering it only from this branch also keeps it off the
 * paths that do not bounce: a flag-off handback to Ember, which does its own
 * gating, and an unknown provider. Mirrors `ProtectedRoute`'s ref guard.
 */
function PlanDeniedRedirect({ to }: PlanDeniedRedirectProps) {
  const snackbars = useSnackbars();
  const hasShownRef = useRef(false);

  useEffect(() => {
    if (hasShownRef.current) {
      return;
    }
    hasShownRef.current = true;
    snackbars.create({
      id: 'route.accessDenied',
      message: NO_WAREHOUSE_ACCESS_MESSAGE,
      variant: 'critical',
      autoCloseTimeout: 10_000,
    });
  }, [snackbars]);

  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

type TypedWarehouseRouteProps = {
  type: string;
  children: (workspaceId: string, destination: WarehouseDestination) => ReactNode;
};

/**
 * The frame both `:type` routes share, on top of `WarehouseRouteFrame`: narrow the
 * route param to a real destination, then apply the warehouse-sync plan gate Ember
 * puts on this subtree (and only this subtree — the connector landing pages stay
 * readable without the feature).
 */
function TypedWarehouseRoute({ type, children }: TypedWarehouseRouteProps) {
  const auth = useAuth();

  return (
    <WarehouseRouteFrame>
      {(workspaceId) => {
        const integrationsHref = generatePath('/workspaces/:workspaceId/journeys/integrations', { workspaceId });

        // An unknown provider is not a page in either app.
        const destination = toWarehouseDestination(type);
        if (destination == null) {
          return <SafeNavigate to={integrationsHref} options={{ shouldReplaceHistoryEntry: true }} />;
        }

        if (auth.account != null && !hasPlanFeatureEnabled(auth.account, PlanFeature.WarehouseSync)) {
          return <PlanDeniedRedirect to={integrationsHref} />;
        }

        return children(workspaceId, destination);
      }}
    </WarehouseRouteFrame>
  );
}

const NO_WAREHOUSE_ACCESS_MESSAGE =
  "You don't have access to data warehouse sync. Contact your admin to update your plan.";

/**
 * The migrated `/integrations/warehouse/:type` config view. Unlike the connector landing pages,
 * this route carries the warehouse-sync plan gate — Ember puts it on the `:type` subtree only.
 */
export function WarehouseConfigRouteView({ type }: WarehouseConfigRouteViewProps) {
  return (
    <TypedWarehouseRoute type={type}>
      {(workspaceId, destination) => <WarehouseConfigBody workspaceId={workspaceId} destination={destination} />}
    </TypedWarehouseRoute>
  );
}

type JourneysWarehouseCreateHandoffProps = {
  type: string;
};

/** The legacy `/journeys/integrations/warehouse/:type/sync` URL. */
export function JourneysWarehouseCreateHandoff({ type }: JourneysWarehouseCreateHandoffProps) {
  const to = useTypedHandoffHref(CREATE_ROUTE_PATH, type);
  const destination = toWarehouseDestination(type);

  // An unknown provider is not a page in the migrated wizard, so forwarding there
  // would land on nothing. Serve Ember, which owns whatever this URL is.
  // (`PROVIDER_FORMS` is total over `WarehouseDestination`, so a resolved
  // destination always has a form — only an unrecognised `type` reaches here.)
  if (destination == null) {
    return <Journeys />;
  }
  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }
  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

type WarehouseCreateBodyProps = {
  workspaceId: string;
  destination: WarehouseDestination;
};

function WarehouseCreateBody({ workspaceId, destination }: WarehouseCreateBodyProps) {
  // The wizard has no crumb of its own: it inherits the destination's, one level up.
  useWarehouseConfigBreadcrumbs(workspaceId, destination);
  const router = useRouter();
  const workspace = useCurrentWorkspace();
  const isTwilioEnabled = useIsTwilioEnabled(workspaceId);
  const { sync, fallback } = useWarehouseSyncPageState(workspaceId);

  const configHref = router.buildLocation({
    to: CONFIG_ROUTE_PATH,
    params: { workspaceId, type: destination },
  }).href;

  if (fallback != null) {
    return fallback;
  }

  // A workspace syncs to one storage service, so the wizard is not reachable once
  // one exists — the config view owns it from then on.
  if (sync != null) {
    return <SafeNavigate to={configHref} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return (
    <RumPageRendered>
      <CreatePage
        // Keyed on the destination: the wizard holds a draft config, a step and an
        // export selection, none of which mean anything for a different provider.
        // Re-rendering in place would offer the GCS form over an S3 draft.
        key={destination}
        workspaceId={workspaceId}
        workspaceName={workspace?.name ?? ''}
        destination={destination}
        isTwilioEnabled={isTwilioEnabled}
        onCancel={() => router.navigate({ to: CONFIG_ROUTE_PATH, params: { workspaceId, type: destination } })}
        onCreated={() => {
          // No invalidation: `useCreateWarehouseSync` already cached the created record, and a
          // refetch that loses the read-after-write race answers 404 — which this layer maps to
          // `null`, so the config page would paint its blank slate over the sync just created.
          void router.navigate({ to: CONFIG_ROUTE_PATH, params: { workspaceId, type: destination } });
        }}
      />
    </RumPageRendered>
  );
}

type WarehouseCreateRouteViewProps = {
  type: string;
};

/** The migrated `/integrations/warehouse/:type/sync` connect wizard. */
export function WarehouseCreateRouteView({ type }: WarehouseCreateRouteViewProps) {
  return (
    <TypedWarehouseRoute type={type}>
      {(workspaceId, destination) => <WarehouseCreateBody workspaceId={workspaceId} destination={destination} />}
    </TypedWarehouseRoute>
  );
}
